// SigninForm.jsx (FULLY FIXED — redirect preserved on errors, 6h inactivity compatible)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Footer from '../../components/footer.js';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthProvider.js';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Swal from 'sweetalert2';

// Helper function to get API base URL based on subdomain
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;

  // For local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://hms.backend.hiragroupcloud.com';
  }

  // Always point to main backend
  return 'https://hms.backend.hiragroupcloud.com';
};

// Helper function to get current subdomain from frontend URL
const getCurrentSubdomain = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Check if this is a subdomain of hms.hiragroupcloud.com
  // Pattern: {tenant}.hms.hiragroupcloud.com
  if (parts.length >= 4 && parts[parts.length - 2] === 'hms' && parts[parts.length - 1] === 'hiragroupcloud.com') {
    return parts[0];
  }

  // Check for other patterns
  if (parts.length > 2) {
    return parts[0];
  }

  return null;
};

// ✅ NEW HELPER: Clear only auth keys, keep redirectAfterLogin
const clearAuthDataKeepingRedirect = () => {
  const redirectPath = localStorage.getItem('redirectAfterLogin');

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userLabId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('userData');
  localStorage.removeItem('userProfile');
  localStorage.removeItem('labInfo');
  localStorage.removeItem('userAddress');
  localStorage.removeItem('labName');
  localStorage.removeItem('labDetails');
  localStorage.removeItem('currentTenant');
  localStorage.removeItem('technicianStamp');
  localStorage.removeItem('userId');

  if (redirectPath) {
    localStorage.setItem('redirectAfterLogin', redirectPath);
  }
};

// ✅ NEW HELPER: Validate the stored redirect path before using it
const isValidRedirectPath = (path) => {
  if (!path) return false;
  if (path === '/' || path === '/login') return false;
  if (path.startsWith('/reset')) return false;
  return path.startsWith('/');
};

const BackgroundContainer = styled.div`
  background-color: azure;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 6px solid #004aad;
  padding: 40px 20px;
`;

const FormCard = styled.div`
  background-color: rgba(0, 74, 173, 0.6);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 40px;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  color: white;
  text-align: center;
`;

const Title = styled.h2`
  margin-bottom: 30px;
  font-weight: 600;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
  text-align: left;
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-size: 15px;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  font-size: 15px;
  background-color: rgba(255, 255, 255, 0.15);
  color: #fff;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    outline: 2px solid #00b4d8;
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
`;

const EyeIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #fff;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #ffaaaa;
  margin-top: 6px;
`;

const RadioGroup = styled.div`
  text-align: left;
  margin-top: 10px;

  label {
    margin-left: 8px;
    font-size: 14px;
    cursor: pointer;
  }

  input[type="radio"] {
    margin-top: 10px;
    cursor: pointer;
    accent-color: #00b4d8;
  }
`;

const Button = styled.button`
  width: 100%;
  margin-top: 25px;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(135deg, #004aad, #00b4d8);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0077b6, #00c2ff);
    transform: scale(1.03);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const StyledLink = styled(Link)`
  display: block;
  margin-top: 20px;
  font-size: 14px;
  color: white;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: #00b4d8;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(46, 204, 113, 0.2);
  border: 1px solid #2ecc71;
  color: #2ecc71;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const TenantInfo = styled.div`
  background-color: rgba(0, 180, 216, 0.2);
  border: 1px solid #00b4d8;
  color: #00b4d8;
  padding: 8px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 12px;
  text-align: center;
`;

const SigninForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [security, setSecurity] = useState('private');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Detect tenant from URL on component mount
  useEffect(() => {
    const currentSubdomain = getCurrentSubdomain();
    const hostname = window.location.hostname;

    if (currentSubdomain) {
      setTenantInfo({
        subdomain: currentSubdomain,
        fullHostname: hostname,
        isTenant: true
      });
    } else {
      setTenantInfo({
        subdomain: null,
        fullHostname: hostname,
        isTenant: false,
        message: 'Main portal - select your organization'
      });
    }

    // Store the intended destination if coming from a protected route
    // (used only for logging/debug; actual redirect uses localStorage)
    const from = location.state?.from?.pathname || localStorage.getItem('redirectAfterLogin');
  }, [location]);

  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = () => {
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginSuccess(false);

    const isEmailValid = validateEmail();
    const isPasswordValid = validatePassword();

    if (isEmailValid && isPasswordValid) {
      setIsLoading(true);
      try {
        const apiBaseUrl = getApiBaseUrl();
        const currentSubdomain = getCurrentSubdomain();

        const headers = {
          'Content-Type': 'application/json',
          'X-Tenant-Subdomain': currentSubdomain || ''
        };

        if (currentSubdomain) {
          headers['X-Original-Host'] = window.location.hostname;
        }

        const response = await axios.post(`${apiBaseUrl}/api/auth/login`, {
          email: email.trim(),
          password: password
        }, { headers });

        if (response.data.success) {
          const { user, accessToken, refreshToken } = response.data;

          const activeLabId = user.activeLabId || user.labId || user.addedBy || '';

          // Store all auth data
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken || '');
          localStorage.setItem('userRole', user.role || '');
          localStorage.setItem('userLabId', activeLabId);
          localStorage.setItem('userEmail', user.email || '');
          localStorage.setItem('userName', `${user.first_name || ''} ${user.last_name || ''}`.trim());
          localStorage.setItem('userData', JSON.stringify(user));

          // Store complete user profile data
          localStorage.setItem('userProfile', JSON.stringify({
            userId: user.userId || '',
            userLabId: user.userLabId || activeLabId,
            role: user.role || '',
            salutation: user.salutation || '',
            firstName: user.first_name || '',
            middleInitial: user.middleInitial || '',
            lastName: user.last_name || '',
            email: user.email || '',
            username: user.username || '',
            phone: user.telephone || '',
            timeZone: user.timeZone || 'UTC',
            status: user.status || 'active',
            createdAt: user.createdAt || '',
            updatedAt: user.updatedAt || ''
          }));

          // Store lab/company information
          localStorage.setItem('labInfo', JSON.stringify({
            labId: user.labId || user.userLabId || activeLabId || '',
            labName: user.labName || '',
            labEmail: user.labEmail || '',
            address: user.address || '',
            address2: user.address2 || '',
            city: user.city || '',
            state: user.state || user.stateProvince || '',
            stateProvince: user.stateProvince || user.state || '',
            zipPostal: user.zipPostal || user.zipCode || '',
            zipCode: user.zipCode || user.zipPostal || '',
            country: user.country || '',
            telephone: user.telephone || '',
            subdomain: user.subdomain || currentSubdomain || '',
            services: user.services || [],
            timeZone: user.timeZone || 'UTC',
            utc: user.utc || ''
          }));

          // Store address information separately for easy access
          localStorage.setItem('userAddress', JSON.stringify({
            address: user.address || '',
            address2: user.address2 || '',
            city: user.city || '',
            state: user.state || user.stateProvince || '',
            zipPostal: user.zipPostal || user.zipCode || '',
            country: user.country || '',
            fullAddress: [
              user.address,
              user.address2,
              user.city,
              user.state || user.stateProvince,
              user.zipPostal || user.zipCode,
              user.country
            ].filter(Boolean).join(', ')
          }));

          // Store lab name and details
          localStorage.setItem('labName', user.labName || '');
          localStorage.setItem('labDetails', JSON.stringify({
            labName: user.labName || '',
            labEmail: user.labEmail || '',
            labId: user.labId || user.userLabId || activeLabId || '',
            address: user.address || '',
            city: user.city || '',
            state: user.state || user.stateProvince || '',
            zipCode: user.zipPostal || user.zipCode || '',
            country: user.country || '',
            telephone: user.telephone || ''
          }));

          localStorage.setItem('currentTenant', JSON.stringify({
            subdomain: currentSubdomain,
            labId: activeLabId,
            isTenant: !!currentSubdomain
          }));

          // ============================================
          // ✅ REDIRECT DESTINATION (FIXED)
          // ============================================
          let redirectPath = '/home';

          const storedRedirect = localStorage.getItem('redirectAfterLogin');
          const stateRedirect = location.state?.from?.pathname;

          if (isValidRedirectPath(storedRedirect)) {
            redirectPath = storedRedirect;
            localStorage.removeItem('redirectAfterLogin');
          } else if (isValidRedirectPath(stateRedirect)) {
            redirectPath = stateRedirect;
          } else {
            // Clean up any invalid stored redirect to avoid future issues
            if (storedRedirect) localStorage.removeItem('redirectAfterLogin');
          }

          // Show success alert
          await Swal.fire({
            icon: 'success',
            title: 'Login Successful!',
            text: `Welcome back! Redirecting to ${redirectPath}...`,
            timer: 1500,
            showConfirmButton: false,
            backdrop: true,
            allowOutsideClick: false
          });

          setLoginSuccess(true);

          // ✅ Call AuthProvider.login BEFORE navigation so silent-refresh timer starts
          login(accessToken, user);

          // Force navigation to the intended destination
          setTimeout(() => {
            navigate(redirectPath, { replace: true });
          }, 1000);
        } else {
          // Handle unsuccessful login
          const errorMessage = response.data.message || 'Login failed';
          setLoginError(errorMessage);

          await Swal.fire({
            icon: 'error',
            title: 'Login Failed',
            text: errorMessage,
            confirmButtonColor: '#004aad',
            confirmButtonText: 'Try Again'
          });
        }
      } catch (error) {
        // ✅ FIXED: Preserve redirectAfterLogin (don't wipe it on network/server error)
        clearAuthDataKeepingRedirect();

        let errorMessage = 'Login failed. Please try again.';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message === 'Network Error') {
          errorMessage = 'Network error. Please check your connection.';
        }

        setLoginError(errorMessage);

        await Swal.fire({
          icon: 'error',
          title: 'Login Error',
          text: errorMessage,
          confirmButtonColor: '#004aad',
          confirmButtonText: 'OK'
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      // Show validation error alert
      if (!isEmailValid && !isPasswordValid) {
        await Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Please enter a valid email address and password (minimum 6 characters).',
          confirmButtonColor: '#004aad',
          confirmButtonText: 'OK'
        });
      } else if (!isEmailValid) {
        await Swal.fire({
          icon: 'warning',
          title: 'Invalid Email',
          text: 'Please enter a valid email address.',
          confirmButtonColor: '#004aad',
          confirmButtonText: 'OK'
        });
      } else if (!isPasswordValid) {
        await Swal.fire({
          icon: 'warning',
          title: 'Invalid Password',
          text: 'Password must be at least 6 characters long.',
          confirmButtonColor: '#004aad',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Check if there's registration success message from previous page
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const registered = searchParams.get('registered');

    if (registered === 'true') {
      setLoginSuccess('Registration successful! Please login with your credentials.');
      Swal.fire({
        icon: 'success',
        title: 'Registration Successful!',
        text: 'Your account has been created. Please login with your credentials.',
        confirmButtonColor: '#004aad',
        confirmButtonText: 'Continue'
      });
    }

    // ✅ FIXED: Clear only auth data on mount — preserve redirectAfterLogin
    // This runs when user lands on login page (either fresh or after logout/expiry)
    clearAuthDataKeepingRedirect();
  }, []);

  return (
    <>
      <BackgroundContainer>
        <FormCard>
          <Title>Login to Your Account</Title>

          {/* Show tenant info if available */}
          {tenantInfo && tenantInfo.isTenant && (
            <TenantInfo>
              <span style={{ color: "white" }}>
                🔒 Logging in to: <strong>{tenantInfo.subdomain}</strong>
              </span>
            </TenantInfo>
          )}

          {tenantInfo && !tenantInfo.isTenant && (
            <TenantInfo>

            </TenantInfo>
          )}

          {loginSuccess && (
            <SuccessMessage>
              {typeof loginSuccess === 'string' ? loginSuccess : 'Login successful! Redirecting...'}
            </SuccessMessage>
          )}

          {loginError && <ErrorText style={{ textAlign: 'center', marginBottom: '20px' }}>{loginError}</ErrorText>}

          <form onSubmit={handleSubmit}>
            <FieldGroup>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onBlur={validateEmail}
                required
                disabled={isLoading}
              />
              {emailError && <ErrorText>{emailError}</ErrorText>}
            </FieldGroup>

            <FieldGroup>
              <Label>Password *</Label>
              <PasswordInputContainer>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                  }}
                  onBlur={validatePassword}
                  required
                  disabled={isLoading}
                />
                <EyeIcon onClick={togglePasswordVisibility}>
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </EyeIcon>
              </PasswordInputContainer>
              {passwordError && <ErrorText>{passwordError}</ErrorText>}
            </FieldGroup>

            <RadioGroup>
              <Label>Security Option</Label>
              <div>
                <input
                  type="radio"
                  id="public"
                  name="security"
                  value="public"
                  checked={security === 'public'}
                  onChange={() => setSecurity('public')}
                  disabled={isLoading}
                />
                <label htmlFor="public">Public or shared computer</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="private"
                  name="security"
                  value="private"
                  checked={security === 'private'}
                  onChange={() => setSecurity('private')}
                  disabled={isLoading}
                />
                <label htmlFor="private">Private computer</label>
              </div>
            </RadioGroup>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <StyledLink to="/reset">Lost your password?</StyledLink>
          {/* <StyledLink to="/signup">
            Don't have an account? <strong>Sign up</strong>
          </StyledLink> */}
        </FormCard>
      </BackgroundContainer>
      <Footer />
    </>
  );
};

export default SigninForm;